import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Input } from 'ncoded-component-library';
import { FieldRenderProps } from 'react-final-form';
import { ExtractPropTypes } from 'types';
import { useTranslation } from 'react-i18next';
import utils from 'utils';
import useCallbackRef from 'hooks/useCallbackRef';
import VerifiedBadge from 'components/VerifiedBadge';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './InputField.styles.scss';

type InputProps = ExtractPropTypes<typeof Input>;

type InputStyleType = 'default' | 'underlined';
type NumberStyle = 'default' | 'dottedNumber' | 'phoneNumber';

export type ResInputProps = FieldWrapperProps &
  InputProps & {
    styleType?: InputStyleType;
    numberStyle?: NumberStyle;
    isVerified?: boolean;
    showInitialError?: boolean;
    customOnBlur?: (e: React.FocusEvent, isValid?: boolean) => void;
    onChangeHandle?: (value: string) => string;
  };

export type InputFieldProps = ResInputProps &
  FieldRenderProps<string, HTMLElement>;

const InputField: React.ForwardRefRenderFunction<
  HTMLInputElement,
  InputFieldProps
> = (props, ref) => {
  const {
    className,
    input: { onChange, onBlur, ...restInput },
    meta: { touched, error },
    withoutStrengthIndicator,
    styleType = 'default',
    numberStyle = 'default',
    readOnly,
    maxLength,
    showErrorMsg = true,
    showInitialError = false,
    isVerified = false,
    onChangeHandle,
    customOnBlur,
    ...rest
  } = props;
  const { t } = useTranslation();

  const [dottedNumbers, phoneNumber] = useMemo(
    () => [numberStyle === 'dottedNumber', numberStyle === 'phoneNumber'],
    [numberStyle],
  );

  const [input, inputRef] = useCallbackRef<HTMLInputElement>();

  const hasError = useMemo(
    () => showErrorMsg && (touched || showInitialError) && error,
    [showErrorMsg, error, showInitialError, touched],
  );

  useImperativeHandle(ref, () => input, [input]);

  const classes = classNames(
    'input-field',
    {
      'input-field--error': hasError,
    },
    {
      'input-field--read-only': readOnly,
    },
    styleType,
    className,
  );

  useEffect(() => {
    if (!restInput) return;

    if (dottedNumbers && restInput.value) {
      const newValue = utils.getDotedNumber(
        restInput.value.toString().replace(/[,]/g, ''),
      );
      onChange(newValue);
      return;
    }

    if (phoneNumber) {
      if (!restInput.value) {
        onChange('');
        return;
      }

      let newValue;
      const val = restInput.value.replace(/[ ]/g, '');
      newValue = val;

      if (restInput.value[0] === '+') {
        newValue = val.replace(/.{3}/g, '$& ');
      } else if (restInput.value[0] === '0') {
        const [prefix, number] = [val.slice(0, 4), val.slice(4)];
        const newNum = number.replace(/.{3}/g, '$& ');

        newValue = `${prefix} ${newNum}`;
      }

      onChange(newValue.trim());
      return;
    }
  }, [dottedNumbers, onChange, phoneNumber, restInput]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const patchedValue =
        onChangeHandle?.(event.target.value) || event.target.value;
      if (dottedNumbers) {
        const newValue = utils.getDotedNumber(patchedValue.replace(/[,]/g, ''));
        onChange(newValue);
        return;
      }

      if (phoneNumber) {
        if (!patchedValue) {
          onChange('');
          return;
        }

        let newValue;
        const val = patchedValue.replace(/[ ]/g, '').trim();
        newValue = val;

        if (patchedValue[0] === '+') {
          newValue = val.replace(/.{3}/g, '$& ');
        } else {
          let newNum;

          if (patchedValue[0] === '0') {
            newNum = val.slice(1).replace(/.{3}/g, '$& ');
          } else if (val.slice(0, 3) === '610') {
            newNum = val.slice(3).replace(/.{3}/g, '$& ');
          } else {
            newNum = val.replace(/.{3}/g, '$& ');
          }

          newValue = `+61 ${newNum}`;
        }
        onChange(newValue.trim());
        return;
      }

      const updatedEvent = {
        ...event,
        target: { ...event.target, value: patchedValue },
      };

      onChange(updatedEvent);
    },
    [dottedNumbers, onChange, onChangeHandle, phoneNumber],
  );

  const onLabelClick = useCallback(() => input.focus(), [input]);

  return (
    <FieldWrapper
      className={classes}
      error={error}
      maxLength={maxLength}
      hasError={hasError}
      onLabelClick={onLabelClick}
      {...restInput}
      {...rest}
    >
      <div className="input-field__wrapper">
        <Input
          hasError={hasError}
          inputMode={dottedNumbers ? 'numeric' : 'text'}
          onChange={handleOnChange}
          readOnly={readOnly}
          maxLength={maxLength}
          onBlurCapture={onBlur}
          onBlur={(e) => customOnBlur?.(e, !hasError)}
          {...restInput}
          {...rest}
          ref={inputRef}
        />

        {isVerified && <VerifiedBadge text={t('verified')} />}
      </div>
    </FieldWrapper>
  );
};

export default forwardRef(InputField);
